import React from "react";
import { formatTime } from "lib/utils/socialtravel";
import { resolveSaleWindowOffset } from "lib/utils/saleWindow";
import FormGroup from "./FormGroup";
import Checkbox from "./Checkbox";
import DateTime from "./DateTime";
import Select from "./Select";
import TimeDuration from "./TimeDuration";

const DEFAULT_OFFSET_SECONDS = 24 * 60 * 60;

// One edge of a sale window ("Otwarcie sprzedaży" / "Zamknięcie sprzedaży").
// Off by default; once enabled the operator picks one of two mutually
// exclusive rules:
//
//   * offset_seconds — "z wyprzedzeniem", counted back from the slot start,
//   * at             — a fixed datetime ("od daty" / "do daty").
//
// The offset is resolved into a concrete datetime per slot on save, but the
// rule itself is kept on the schedule, so re-opening a saved series shows the
// offset again rather than the datetime it happened to resolve to. Callers
// that know which date is being edited pass `slotStartsAt`, and the datetime
// that offset resolves to for that date is previewed under the row — the
// series still gets one window per date, the preview just names the one for
// the date on screen. The per-variant defaults have no date to count back
// from and pass nothing, so no preview is rendered there.
export default function SaleWindowRuleFormGroup({
  error,
  valid = !error,
  required,
  wrapperClassName,
  help,
  disabled,
  readOnly,
  label,
  edge = "start",
  value: { offset_seconds = null, at = null } = {},
  onFocus,
  onBlur,
  onChange,
  timeZoneName,
  slotStartsAt = null,
}) {
  const options = [
    ["offset_seconds", I18n.t("papp.components.SaleWindowRuleFormGroup.offset_label")],
    [
      "at",
      edge === "end"
        ? I18n.t("papp.components.SaleWindowRuleFormGroup.until_date_label")
        : I18n.t("papp.components.SaleWindowRuleFormGroup.from_date_label"),
    ],
  ];

  let currentOption = null;
  if (offset_seconds !== null) {
    currentOption = "offset_seconds";
  } else if (at !== null) {
    currentOption = "at";
  }

  const defaultAt = () => at || moment.tz(timeZoneName).format(moment.RUBY_DATETIME_FORMAT);

  const resolvedAt = resolveSaleWindowOffset({ slotStartsAt, offsetSeconds: offset_seconds, timeZoneName });

  const handleSelectChange = (nextOption) => {
    if (nextOption === "offset_seconds") {
      onChange({ offset_seconds: offset_seconds === null ? DEFAULT_OFFSET_SECONDS : offset_seconds, at: null });
    } else if (nextOption === "at") {
      onChange({ offset_seconds: null, at: defaultAt() });
    } else {
      onChange({ offset_seconds: null, at: null });
    }
  };

  return (
    <FormGroup error={error} valid={valid} required={required} className={wrapperClassName}>
      <Checkbox
        label={label}
        required={required}
        checked={currentOption !== null}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(checked) =>
          checked ? handleSelectChange("offset_seconds") : onChange({ offset_seconds: null, at: null })
        }
        toggle
        disabled={disabled}
        readOnly={readOnly}
      />

      <span className="field inline u-marginLeft" style={{ visibility: currentOption === null ? "hidden" : undefined }}>
        <Select
          value={currentOption}
          onChange={handleSelectChange}
          options={options}
          disabled={disabled}
          readOnly={readOnly}
          className="u-smallWidth"
        />

        {currentOption === "offset_seconds" && (
          <span>
            <TimeDuration
              value={offset_seconds}
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={(nextOffsetSeconds) => onChange({ offset_seconds: nextOffsetSeconds, at: null })}
              durationTypeTimeLengths={[24 * 60 * 60, 60 * 60, 60]}
              disabled={disabled}
              readOnly={readOnly}
            />
            &nbsp; {I18n.t("papp.components.SaleWindowRuleFormGroup.before_slot_start")}
          </span>
        )}

        {currentOption === "at" && (
          <DateTime
            value={at}
            timeZoneName={timeZoneName}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={(nextAt) => onChange({ offset_seconds: null, at: nextAt })}
            disabled={disabled}
            readOnly={readOnly}
          />
        )}
      </span>

      {currentOption === "offset_seconds" && resolvedAt && (
        <div className="ui help">
          {I18n.t("papp.components.SaleWindowRuleFormGroup.resolved_preview", {
            slot: formatTime(slotStartsAt, { format: "short" }),
            resolved: formatTime(resolvedAt, { format: "short" }),
          })}
        </div>
      )}

      {!!help && <div className="ui help">{help}</div>}
    </FormGroup>
  );
}



// WEBPACK FOOTER //
// ./src/lib/components/FormControl/SaleWindowRuleFormGroup.jsx